'use client';

import MobileDrawer from '@c/drawers/MobileDrawer';
import BaseModal from '@c/modals/BaseModal';
import {
  createChat,
  getNextCaseNumber,
  sendMessageToChat,
} from '@util/firestore/messages';
import { isMobile } from '@util/index';
import { useAuth } from 'context/AuthContext';
import { useChat } from 'context/ChatContext';
import { HelpCircleIcon, Loader2 } from 'lucide-react';
import { usePathname } from 'next/navigation';
import { useRouter } from '@bprogress/next';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import useRealtimeChats from '@util/hooks/useRealtimeChats';

type HelpWithVehicleModalProps = {
  isOpen: boolean;
  dismiss: () => void;
  onCloseAllModals: () => void;
};

const helpFormSchema = z.object({
  bike: z.string().min(1, 'Please enter your vehicle details'),
});

const HelpWithVehicleForm = ({
  onCloseAllModals,
}: {
  onCloseAllModals: () => void;
}) => {
  const { userDoc } = useAuth();
  const { openChatDrawer } = useChat();
  const router = useRouter();
  const pathname = usePathname();
  const { realtimeChats } = useRealtimeChats(userDoc);
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    reset,
  } = useForm<z.infer<typeof helpFormSchema>>({
    defaultValues: { bike: '' },
    resolver: zodResolver(helpFormSchema),
    mode: 'onChange',
  });

  const onSubmitSupportHelp = async (data: z.infer<typeof helpFormSchema>) => {
    if (!userDoc) {
      router.push(`/login?redirect=${encodeURIComponent(pathname ?? '')}`);
      return;
    }

    setIsLoading(true);

    const existingSupportChat =
      realtimeChats?.find(
        (chat) =>
          chat.uids?.includes(userDoc?.uid ?? '') &&
          chat.uids?.includes(process.env.NEXT_PUBLIC_SUPPORT_ID ?? '')
      ) ??
      (await createChat({
        is_offer: false,
        uids: [userDoc.uid, process.env.NEXT_PUBLIC_SUPPORT_ID ?? ''],
        buyer_id: userDoc.uid,
        seller_id: process.env.NEXT_PUBLIC_SUPPORT_ID ?? '',
        last_time: Date.now(),
        created_at: Date.now(),
        messages: [],
        from_web: true,
        is_auto: false,
        unread: {
          [process.env.NEXT_PUBLIC_SUPPORT_ID ?? '']: true,
        },
        id: '', // placeholder for id, get's created in the createChat function
      }));

    const messageContent = `Hi, I'm trying to find parts for my ${data.bike}, but I couldn't find this vehicle in the search results. Can you help me out?`;

    const message = {
      content: messageContent,
      uid: userDoc?.uid ?? '',
      created_at: Date.now(),
      is_img: false,
      is_auto: false,
    };

    await sendMessageToChat(message, existingSupportChat, userDoc);

    if (isMobile()) {
      router.push(`/dashboard/messages/${existingSupportChat.id}`);
    } else {
      openChatDrawer(existingSupportChat);

      onCloseAllModals();
    }

    setIsLoading(false);
  };

  return (
    <div className="flex flex-col gap-y-8 p-8">
      <p className="w-[41rem] text-[1.8rem] text-zinc-600">
        If you didn't find your vehicle in the list, please contact our support
        and request us to add it.
      </p>

      <input
        type="text"
        className="h-[5.5rem] w-full rounded-[1.5rem] border border-zinc-200 bg-zinc-100 px-[2rem] text-[1.8rem] text-zinc-700 placeholder:text-zinc-500"
        placeholder="Your vehicle year, make & model"
        {...register('bike')}
      />

      {errors.bike && (
        <p className="text-[1.6rem] font-medium text-red-500">
          {errors.bike.message}
        </p>
      )}

      <div className="flex w-full justify-end">
        <button
          type="button"
          className="duration-50 flex h-[5.5rem] w-fit items-center gap-x-4 rounded-full bg-brand-secondary px-[2rem] text-[1.8rem] font-medium text-white transition-all ease-linear disabled:grayscale"
          onClick={handleSubmit(onSubmitSupportHelp)}
          disabled={isLoading}
        >
          {isLoading ? (
            <div className="flex items-center gap-x-2">
              <Loader2 className="h-8 w-8 animate-spin text-white" />
              <span className="text-[1.8rem] font-medium text-white">
                Sending...
              </span>
            </div>
          ) : (
            <div className="flex items-center gap-x-2">
              <HelpCircleIcon className="h-8 w-8 stroke-white" />
              Open support ticket
            </div>
          )}
        </button>
      </div>
    </div>
  );
};

export default function HelpWithVehicleModal({
  isOpen,
  dismiss,
  onCloseAllModals,
}: HelpWithVehicleModalProps) {
  if (isMobile()) {
    return (
      <MobileDrawer show={isOpen} dismiss={dismiss} title="Help with Vehicle">
        <HelpWithVehicleForm onCloseAllModals={onCloseAllModals} />
      </MobileDrawer>
    );
  }

  return (
    <BaseModal isOpen={isOpen} dismiss={dismiss} title="Help with Vehicle">
      <HelpWithVehicleForm onCloseAllModals={onCloseAllModals} />
    </BaseModal>
  );
}
